import { ThemeProvider, css } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { isBefore } from "date-fns";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { Dispatch } from "redux";
import { Descendant } from "slate";

import { PopperTooltip } from "@livechat/design-system";
import { ChevronLeft, ChevronRight, Menu } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, Loader } from "@livechat/design-system-react-components";

import CircleBadge from "components/circle-badge";
import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import { IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { ICannedResponsesProps } from "components/message-box/canned-responses";
import ShortcutTooltip from "components/shortcut-tooltip/ShortcutTooltip";
import { KeyModifier, Shortcuts } from "components/shortcuts/Shortcuts";
import { newFeaturesCampaigns } from "constants/campaigns";
import { KeyCodeEnum } from "constants/keyCode";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import Role from "constants/role";
import { TicketsRoutes, getPathForRoute } from "constants/tickets/TicketsRoutes";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isMac } from "helpers/isMac";
import { setDocumentTitle } from "helpers/routing";
import { getIsMemberOfTicketTeam } from "helpers/tickets";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { ITicket, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { actions$ } from "store/action-stream";
import { IEntityFetchSuccess, RequestAction } from "store/entities/actions";
import {
  AgentActions,
  IAgent,
  selectAgentsAlphabetically,
  selectHasSeenNavigationInTicketTooltip,
  selectLoggedInAgent,
} from "store/entities/agents";
import { selectAgentCannedResponsesByShortcuts } from "store/entities/canned-responses";
import { ILicenseSettings, selectLicenseSettings, selectTicketFeedToolbar } from "store/entities/license";
import { IRule } from "store/entities/rules/interfaces";
import { selectRules } from "store/entities/rules/selectors";
import { ITag, selectAgentTagsAlphabetically, selectTagsAlphabetically } from "store/entities/tags";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { TICKET, TicketActions } from "store/entities/tickets/actions";
import { IFetchClosestTicketInfoPayload, IFetchTicketsToMergePayload } from "store/entities/tickets/interfaces";
import { getTagsUsageAllowed } from "store/features/session";
import { selectTicketViewers } from "store/features/ticket-activity/selectors";
import { IActionWithPayload } from "store/helper";
import { TicketsViewActions } from "store/views/tickets/actions";
import { IMergeTicketsPayload, ITicketsViewSendMessagePayload } from "store/views/tickets/interfaces";
import {
  getFocusedColumn,
  getRoute,
  getTicket,
  isSendingMessage,
  isUnspamming,
  selectCanDeleteTickets,
  selectCanManageTickets,
  selectIsFetchingClosestTicket,
  selectRecentTickets,
  selectTicketsToMerge,
} from "store/views/tickets/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withPx, withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";
import { plural } from "utils/string";

import { ChangeTicketCcsModal } from "./ChangeTicketCcsModal";
import { ChangeTicketRequesterModal } from "./ChangeTicketRequesterModal";
import { ChangeTicketSubjectModal } from "./ChangeTicketSubjectModal";
import { DeleteTicketModal } from "./DeleteTicketModal";
import { MacrosPromoBanner } from "./MacrosPromoBanner";
import MergeTicketModal from "./MergeTicketModal";
import SpamListModal from "./SpamListModal";
import { TicketSiloLabel } from "./TicketAccessSiloLabel";
import TicketBottomSiloLabel from "./TicketBottomSiloLabel";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import TicketItem from "./TicketItem";
import TicketMessageBox from "./TicketMessageBox";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import TicketTitle from "./TicketTitle";
import { Attachment } from "./message-box/legacy/TicketAttachments";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import AttachmentProvider from "./message-box/richtext/AttachmentProvider";
import { StyledAccessWarning, StyledAccessWarningIcon } from "./styles";
import { TicketDetails } from "./ticket-details";
import { TicketToolbar } from "./ticket-toolbar/TicketToolbar";

const ONBOARDING_TICKET_IDS = [
  "6a68fc40-d9a5-4753-8c0c-1edc06f73233",
  "c1aecad3-061c-4208-be85-7a0fee80d0d2",
  "2aa88003-e6ad-467f-8a48-e81ff2aace63",
  "b01353cb-d6c1-4b09-a8c9-6c7b94ab6e41",
];

interface IOwnProps {
  id: string;
}

interface IStateProps {
  ticket: ITicket;
  route: TicketsRoutes;
  agents: IAgent[];
  rules: IRule[];
  cannedResponsesByShortcuts: ICannedResponsesProps["items"];
  currentAgent: IAgent;
  focusedColumn: number;
  teams: ITeam[];
  tags: ITag[];
  allTags: ITag[];
  tagsUsageAllowed: boolean;
  sendingMessage: boolean;
  unspamming: boolean;
  canDeleteTickets: boolean;
  canManageTickets: boolean;
  viewingAgentIDs: string[];
  recentTickets: ITicket[];
  ticketsToMerge: ITicket[];
  isFetchingClosestTicket: boolean;
  hasSeenNavigationInTicketTooltip: boolean;
  shouldSeeTicketToolbar: boolean;
  licenseSettings: ILicenseSettings;
}

interface IDispatchProps {
  onStatusUpdate(id: string, status: TicketStatus, usingShortcut?: boolean): void;
  onTicketOpen(id: string): void;
  onDelete(id: string): void;
  onSiloUpdate(ticketId: string, silo: TicketSilo): void;
  onAssigneeChange(ticketId: string, assigneeId: string): void;
  onAssignedTeamChange(ticketId: string, teamId: string): void;
  onTagsAdd(ticketId: string, tagIds: string[], suggestedTagIds?: string[]): void;
  onTagRemove(ticketId: string, tagId: string): void;
  onSubjectChange(ticketId: string, subject: string): void;
  onRequesterChange(ticketId: string, requesterName: string, requesterEmail: string): void;
  onCcsChange(ticketId: string, ccs: string[]): void;
  onMessageSend(payload: ITicketsViewSendMessagePayload): void;
  onDraftSave(
    ticketId: string,
    message: string | Descendant[],
    isPrivate: boolean,
    status: TicketStatus,
    cannedResponseIds: string[],
    replySuggestionIds: string[]
  ): void;
  onDraftClear(ticketId: string): void;
  onUnspam(ticketId: string): void;
  onTicketUrlCopy(): void;
  onSetFocusedColumn(focusedColumn: number): void;
  notifyTicketViewing(ticketId: string): void;
  notifyStopTicketViewing(ticketId: string): void;
  notifyTicketTyping(ticketId: string): void;
  notifyStopTicketTyping(ticketId: string): void;
  mergeTickets(payload: IMergeTicketsPayload): void;
  fetchTicketsToMerge(payload: IFetchTicketsToMergePayload): void;
  fetchNextTicket(payload: IFetchClosestTicketInfoPayload): void;
  fetchPreviousTicket(payload: IFetchClosestTicketInfoPayload): void;
  setAgentFlag: (name: string, value: boolean) => void;
}

interface IProps extends IOwnProps, IDispatchProps, IStateProps, IWithBreakpointsProps, RouteComponentProps {}

interface IState {
  subjectModal: boolean;
  requesterModal: boolean;
  deleteModal: boolean;
  ccsModal: boolean;
  spamModal: [string, string[]] | null;
  loadingEvents: boolean;
  attachments: Attachment[];
  transactionID: string | null;
  mergingModal: boolean;
  entryLastMessageAt: string;
  isPreviousTicketLoading: boolean;
  isNextTicketLoading: boolean;
}

const LoaderWrapper = styled.div`
  display: flex;
  height: 100px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding-top: 50px;
`;

const OpenDetailsWrapper = styled.div`
  display: none;

  @media (max-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    cursor: pointer;
  }
`;

const TicketWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const TicketItemWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
`;

const TicketMessageBoxWrapper = styled.div`
  @media (min-width: ${withPx(ResponsiveBreakpoint.Mobile + 1)}) {
    box-sizing: border-box;
    margin: 10px auto;
    padding: 0 16px;
    max-width: 900px;
    width: 100%;
  }

  @media (min-width: ${withPx(ResponsiveBreakpoint.Tablet)}) {
    padding: 0 60px;
  }
`;

const AgentAvatarsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: inline-block;
    padding: 0;
    margin: 0;
    margin-left: -6px;
  }

  li:first-of-type {
    margin-left: 0;
  }
`;

const ActivityAvatar = styled.div<{ url?: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${(props): string => (props.url ? props.url : "")});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

const StyledLink = styled(Link)`
  font-size: 13px;
  display: inline;
  font-weight: normal;
  cursor: pointer;
  color: ${withVar(DesignToken.ActionPrimaryDefault)};
  text-decoration: none;
`;

const TicketActivityIndicator: React.FC<{
  agents: IAgent[];
  viewingAgentIDs: string[];
  ticketID: string;
  isMobile: boolean;
  isRecentTicket: boolean;
}> = ({ agents, viewingAgentIDs, isMobile, isRecentTicket }) => {
  const viewers = viewingAgentIDs
    .map(agentID => {
      const viewer = agents.find(a => a.id === agentID);
      return viewer;
    })
    .filter(Boolean);

  return !isMobile && isRecentTicket && viewers.length ? (
    <AgentAvatarsList>
      {viewers.slice(0, 3).map((viewer, index) => (
        <li key={viewer?.id} style={{ zIndex: viewers.length - index }}>
          <PopperTooltip
            placement="bottom"
            zIndex={StackingContextLevel.Tooltip}
            trigger={
              <span>
                <CircleBadge>
                  {/* @ts-expect-error TS(2322) */}
                  <ActivityAvatar url={viewer?.avatar} />
                </CircleBadge>
              </span>
            }
            modifiers={{
              offset: {
                offset: "0, 12",
              },
            }}
          >
            <Text whiteSpace="nowrap">{viewer?.name} is viewing</Text>
          </PopperTooltip>
        </li>
      ))}
      {viewers.length > 3 && (
        <li style={{ zIndex: viewers.length - 3 }}>
          <PopperTooltip
            placement="bottom"
            zIndex={StackingContextLevel.Tooltip}
            trigger={
              <span>
                <CircleBadge>+{viewers.length - 3}</CircleBadge>
              </span>
            }
            modifiers={{
              offset: {
                offset: "0, 12",
              },
            }}
          >
            {viewers.slice(3).map(v => (
              //  @ts-expect-error TS(18048)
              <Text key={v.id} whiteSpace="nowrap">
                {/* @ts-expect-error TS(18048) */}
                {v.name}
              </Text>
            ))}
            <Text whiteSpace="nowrap">{plural(viewers.length - 3, "is", "are")} viewing</Text>
          </PopperTooltip>
        </li>
      )}
    </AgentAvatarsList>
  ) : null;
};

export class UnconnectedTicket extends React.PureComponent<IProps, IState> {
  state = {
    subjectModal: false,
    requesterModal: false,
    deleteModal: false,
    spamModal: null,
    ccsModal: false,
    attachments: [],
    transactionID: null,
    loadingEvents: false,
    mergingModal: false,
    entryLastMessageAt: "",
    isPreviousTicketLoading: false,
    isNextTicketLoading: false,
  };

  reloadScheduled = false;
  ticketFetchSubscription = null;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private setupTicketActivity(longTicketId: string) {
    if (longTicketId) {
      this.props.notifyTicketViewing(longTicketId);
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private disconnectTicketActivity() {
    const { id } = this.props.ticket || {};

    if (id) {
      this.props.notifyStopTicketViewing(id);
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidMount() {
    this.props.onTicketOpen(this.props.id);
    this.setState({ loadingEvents: true });
    trackEngagement("Tickets: Ticket opened", EventPlace.Tickets);

    //  @ts-expect-error TS(2322)
    this.ticketFetchSubscription = pipe(
      actions$,
      filter(
        (action: IActionWithPayload<string, IEntityFetchSuccess<ITicket>>) =>
          action.type === TICKET.FETCH_SINGLE[RequestAction.SUCCESS]
      ),
      subscribe((action: IActionWithPayload<string, IEntityFetchSuccess<ITicket>>) => {
        const fetchedTicket = action.payload;
        if (fetchedTicket.id === this.props.id || fetchedTicket.value.shortId === this.props.id) {
          this.setupTicketActivity(fetchedTicket.id);
          this.setState({ loadingEvents: false });
          if (
            !this.state.entryLastMessageAt &&
            fetchedTicket.value.lastMessageAt !== this.props.ticket?.lastMessageAt
          ) {
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            this.changeEntryLastMessageAt(fetchedTicket.value.lastMessageAt.toISOString());
          }
        }
      })
    );

    if (ONBOARDING_TICKET_IDS.includes(this.props.id)) {
      trackEngagement("Ticket: Onboarding ticket opened", EventPlace.Tickets, { id: this.props.id });
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private changeEntryLastMessageAt = (entryLastMessageAt: string) => {
    this.setState({ entryLastMessageAt });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidUpdate(prevProps: IProps) {
    if (this.props.ticket) {
      setDocumentTitle(this.props.ticket.subject);
    }

    if (prevProps.id !== this.props.id) {
      this.props.onTicketOpen(this.props.id);
    }

    if (prevProps.ticket?.id !== this.props.ticket?.id) {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      this.changeEntryLastMessageAt(this.props.ticket.lastMessageAt.toISOString());
      this.disconnectTicketActivity();
      this.setupTicketActivity(this.props.ticket?.id);
    }

    if (prevProps.isFetchingClosestTicket && !this.props.isFetchingClosestTicket) {
      this.resetClosestTicketLoading();
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    if (this.ticketFetchSubscription) {
      // @ts-expect-error TS(2349): This expression is not callable.
      this.ticketFetchSubscription();
    }

    this.disconnectTicketActivity();
  }

  /**
   * Returns "key" React property to recreate search component when necessary
   * https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
   *
   * Message box is a self-controlled component and we need to re-render it
   * when agent's access to ticket has changed.
   */
  private getMessageBoxReactKey(canManageTicket: boolean): string {
    return `${this.props.ticket.id}__${canManageTicket}`;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleBackToList = () => {
    this.props.onSetFocusedColumn(1);
    this.props.history.push(getPathForRoute(this.props.route));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleOpenDetails = () => {
    this.props.onSetFocusedColumn(1);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  private handleParentClick = (e: any) => {
    e.stopPropagation();
    trackEngagement("Merging tickets: ticket parent link clicked", EventPlace.Tickets);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleMessageSend = (
    message: string | Descendant[],
    isPrivate: boolean,
    status: TicketStatus,
    attachments?: File[],
    transactionID?: string,
    richTextPresent?: boolean,
    mentionedAgents?: string[],
    handleClearMessageBox?: () => void,
    usingShortcut?: boolean,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ) => {
    let attachmentsFlow = {};

    if (transactionID) {
      attachmentsFlow = { transactionID };
    } else {
      attachmentsFlow = { attachments };
    }

    const payload: ITicketsViewSendMessagePayload = {
      ticketId: this.props.ticket.id,
      message,
      isPrivate,
      richTextPresent,
      ...attachmentsFlow,
      ...(mentionedAgents ? { mentionedAgentIDs: mentionedAgents } : {}),
      handleClearMessageBox,
      usingShortcut,
      cannedResponseIds,
      replySuggestionIds,
    };

    if (!isPrivate) {
      payload.status = status;
    }

    this.props.onMessageSend(payload);
  };

  private handleDraftSave = (
    message: string | Descendant[],
    messageText: string,
    isPrivate: boolean,
    status: TicketStatus,
    cannedResponseIds: string[] = [],
    replySuggestionIds: string[] = []
  ): void => {
    if (messageText.trim().length > 0) {
      this.props.onDraftSave(this.props.ticket.id, message, isPrivate, status, cannedResponseIds, replySuggestionIds);
    } else {
      this.props.onDraftClear(this.props.ticket.id);
    }
  };

  private handleAttachmentsChange = (attachments: Attachment[]): void => {
    this.setState({ attachments });
  };

  private handleAssigneeChange = (assigneeId: string): void => {
    if (
      (!this.props.ticket.assignment.agent && assigneeId) ||
      (this.props.ticket.assignment.agent && this.props.ticket.assignment.agent.id !== assigneeId)
    ) {
      this.props.onAssigneeChange(this.props.ticket.id, assigneeId);
    }
  };

  private handleAssignedTeamChange = (teamId: string): void => {
    this.props.onAssignedTeamChange(this.props.ticket.id, teamId);
  };

  private handleTagAdd = (tagId: string, suggested: boolean): void =>
    this.props.onTagsAdd(this.props.ticket.id, [tagId], suggested ? [tagId] : undefined);

  private handleTagRemove = (tagId: string): void => this.props.onTagRemove(this.props.ticket.id, tagId);

  private handleSubjectModalOpen = (): void => {
    this.setState({
      subjectModal: true,
    });
  };

  private handleSubjectModalClose = (): void => {
    this.setState({
      subjectModal: false,
    });
  };

  private handleSubjectModalSubmit = (subject: string): void => {
    this.handleSubjectModalClose();
    this.props.onSubjectChange(this.props.ticket.id, subject);
  };

  private handleRequesterModalOpen = (): void => {
    this.setState({
      requesterModal: true,
    });
  };

  private handleRequesterModalClose = (): void => {
    this.setState({
      requesterModal: false,
    });
  };

  private handleRequesterModalSubmit = (requesterName: string, requesterEmail: string): void => {
    const { requester } = this.props.ticket;
    this.handleRequesterModalClose();
    if (requesterName !== requester.name || requesterEmail !== requester.email) {
      this.props.onRequesterChange(this.props.ticket.id, requesterName, requesterEmail);
    }
  };

  private handleDeleteTicketModalOpen = (): void => {
    this.setState({
      deleteModal: true,
    });
  };

  private handleDeleteModalClose = (): void => {
    this.setState({
      deleteModal: false,
    });
  };

  private handleDeleteModalSubmit = (): void => {
    this.handleDeleteModalClose();
    trackEngagement("Tickets: Delete forever actions", EventPlace.TicketDetails, {
      from: this.props.ticket.silo,
      place: EventPlace.TicketDetails,
    });
    this.props.onDelete(this.props.ticket.id);
  };

  private handleCcsModalOpen = (): void => {
    this.setState({
      ccsModal: true,
    });
  };

  private handleCcsModalClose = (): void => {
    this.setState({
      ccsModal: false,
    });
  };

  private handleCcsModalSubmit = (emails: string[]): void => {
    this.handleCcsModalClose();
    this.props.onCcsChange(this.props.ticket.id, emails);
  };

  private handleTransactionID = (transactionID: string): void => {
    this.setState({ transactionID });
  };

  private handleSpamModalClose = (): void => {
    trackEngagement("Ticket: Spam modal closed without action", EventPlace.Tickets);
    this.setState({ spamModal: null });
  };

  private handleMergingModalClose = (): void => {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'boolean'.
    this.setState({ mergingModal: null });
  };

  private handleMergingModalOpen = (): void => {
    this.setState({
      mergingModal: true,
    });
  };

  private handleStatusUpdate = (status: TicketStatus, usingShortcut?: boolean): void => {
    const { onStatusUpdate, ticket } = this.props;
    onStatusUpdate(ticket.id, status, usingShortcut);
  };

  private onFetchNextTicket = (): void => {
    const { ticket, fetchNextTicket } = this.props;
    const { entryLastMessageAt } = this.state;
    if (!entryLastMessageAt) {
      return;
    }
    this.setState({
      isNextTicketLoading: true,
    });
    fetchNextTicket({
      ticketId: ticket.id,
      value: entryLastMessageAt,
      direction: "next",
    });
    this.onCloseNavigationTootlip();
  };

  private onFetchPreviousTicket = (): void => {
    const { ticket, fetchPreviousTicket } = this.props;
    const { entryLastMessageAt } = this.state;
    if (!entryLastMessageAt) {
      return;
    }
    this.setState({
      isPreviousTicketLoading: true,
    });
    fetchPreviousTicket({
      ticketId: ticket.id,
      value: entryLastMessageAt,
      direction: "prev",
    });
    this.onCloseNavigationTootlip();
  };

  private resetClosestTicketLoading = (): void => {
    this.setState({
      isNextTicketLoading: false,
      isPreviousTicketLoading: false,
    });
  };

  private onCloseNavigationTootlip = (): void => {
    const { setAgentFlag, hasSeenNavigationInTicketTooltip } = this.props;
    if (!hasSeenNavigationInTicketTooltip) {
      setAgentFlag("hasSeenNavigationInTicketTooltip_c38a94dd80116a54bf68d48544c51ebd", true);
      trackEngagement("Ticket details: Navigation tooltip closed");
    }
  };

  private handleShortcutStatusChange = (status: TicketStatus): void => {
    const { ticket, currentAgent, canManageTickets } = this.props;
    const isChildTicket = !!(ticket && ticket.parentTicket);
    const isRecentTicket = ticket?.silo === TicketSilo.Tickets;
    const isMemberOfTicketTeam = ticket && getIsMemberOfTicketTeam(currentAgent, ticket);
    if (!canManageTickets || isChildTicket || !isRecentTicket || !isMemberOfTicketTeam) {
      return;
    }
    this.handleStatusUpdate(status, true);
  };

  private shortcutsConfig = {
    [KeyCodeEnum.ArrowLeft]: {
      additionalKeysRequired: [KeyModifier.Alt],
      disableDefaultBehavior: true,
      eventHandler: (): void => {
        this.onFetchPreviousTicket();
        trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
          moveTo: "previous",
          method: "shortcut",
        });
      },
    },
    [KeyCodeEnum.KeyJ]: {
      disableDefaultBehavior: true,
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],

      eventHandler: (): void => {
        this.onFetchPreviousTicket();
        trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
          moveTo: "previous",
          method: "jkShortcut",
        });
      },
    },
    [KeyCodeEnum.ArrowRight]: {
      additionalKeysRequired: [KeyModifier.Alt],
      disableDefaultBehavior: true,

      eventHandler: (): void => {
        this.onFetchNextTicket();
        trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
          moveTo: "next",
          method: "shortcut",
        });
      },
    },
    [KeyCodeEnum.KeyK]: {
      disableDefaultBehavior: true,
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],

      eventHandler: (): void => {
        this.onFetchNextTicket();
        trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
          moveTo: "next",
          method: "jkShortcut",
        });
      },
    },
    [KeyCodeEnum.KeyS]: {
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      disableDefaultBehavior: true,
      eventHandler: (): void => this.handleShortcutStatusChange(TicketStatus.Solved),
    },
    [KeyCodeEnum.KeyC]: {
      additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
      disableDefaultBehavior: true,
      eventHandler: (): void => this.handleShortcutStatusChange(TicketStatus.Closed),
    },
  };

  private onSiloChange = (silo: TicketSilo): void => {
    const { onSiloUpdate, ticket } = this.props;

    if (silo === TicketSilo.Spam) {
      trackEngagement("Ticket: Blocked list modal opened on silo change", EventPlace.Tickets);
      this.setState({ spamModal: ["spam", [this.props.ticket.requester.email]] });
    }

    if (silo !== TicketSilo.Spam && ticket.silo === TicketSilo.Spam) {
      trackEngagement("Ticket: Trusted list modal opened on silo change", EventPlace.Tickets);
      this.setState({ spamModal: ["unspam", [this.props.ticket.requester.email]] });
    }

    onSiloUpdate(ticket.id, silo);
  };

  render(): React.ReactNode {
    const {
      id,
      ticket,
      agents,
      cannedResponsesByShortcuts,
      currentAgent,
      focusedColumn,
      teams,
      allTags,
      tags,
      rules,
      tagsUsageAllowed,
      sendingMessage,
      canDeleteTickets,
      isMobile,
      canManageTickets,
      onTicketUrlCopy,
      notifyStopTicketTyping,
      notifyTicketTyping,
      viewingAgentIDs,
      mergeTickets,
      ticketsToMerge,
      fetchTicketsToMerge,
      hasSeenNavigationInTicketTooltip,
      isFetchingClosestTicket,
      shouldSeeTicketToolbar,
      licenseSettings,
    } = this.props;

    const {
      attachments,
      subjectModal,
      requesterModal,
      ccsModal,
      deleteModal,
      loadingEvents,
      spamModal,
      mergingModal,
      entryLastMessageAt,
      isNextTicketLoading,
      isPreviousTicketLoading,
    } = this.state;

    const backButton = <BackButton onClick={this.handleBackToList} />;
    const isFetching = ticket == null || (id !== ticket.id && id !== ticket.shortId) || loadingEvents;
    const isMemberOfTicketTeam = ticket && getIsMemberOfTicketTeam(currentAgent, ticket);
    const hasFullTicketAccess = canManageTickets && isMemberOfTicketTeam;
    const canDeleteTicket = canDeleteTickets && (isMemberOfTicketTeam || currentAgent.permission === Role.Owner);
    const isAgentInTicketTeam =
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ticket && !!currentAgent.teamIds.find((agentTeamId: any) => ticket.assignment.team.id === agentTeamId);
    const shouldSeeNavigationInTicketTooltip =
      isBefore(currentAgent.createdAt, newFeaturesCampaigns.hasSeenNavigationInTicketTooltip.campaignStartDate) &&
      !hasSeenNavigationInTicketTooltip;
    const canSendPrivateMsgAsViewer = currentAgent.settings.canSendPrivateMsgAsViewer;
    const canSendPrivateMessage = canManageTickets || canSendPrivateMsgAsViewer;

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let ccsEmails: any = [];
    if (ticket && ticket.ccs) {
      ccsEmails = ticket.ccs.map(cc => cc.email);
    }

    const isChildTicket = !!(ticket && ticket.parentTicket);
    const isRecentTicket = ticket?.silo === TicketSilo.Tickets;
    const isMacUser = isMac(navigator.userAgent);

    return (
      <>
        <Grid relativeWidths={[3, 1]} minWidths={[0, 320]} focusedColumn={focusedColumn} overflowHidden>
          <ViewStack>
            <ThemeProvider theme={legacyTheme}>
              <ViewContainer
                contentCss={css`
                  z-index: auto;
                  height: calc(100% - 52px);
                `}
                title={
                  <TicketTitle
                    isFetching={isFetching}
                    subject={ticket && ticket.subject}
                    canEditSubject={!isChildTicket && hasFullTicketAccess && isRecentTicket}
                    onSubjectModalOpen={this.handleSubjectModalOpen}
                  />
                }
                stackShowBackButton
                backButton={backButton}
                placement="main-content"
                rightNodes={
                  <>
                    <Flex alignItems="center">
                      <Flex alignItems="center" marginRight="1em" gap="16px">
                        <TicketActivityIndicator
                          agents={agents}
                          ticketID={id}
                          viewingAgentIDs={viewingAgentIDs}
                          isMobile={isMobile}
                          isRecentTicket={isRecentTicket}
                        />
                        {!isMobile && !shouldSeeTicketToolbar && (
                          <Shortcuts config={this.shortcutsConfig}>
                            <Flex gap="4px" alignItems="center">
                              <ShortcutTooltip
                                trigger={
                                  <Button
                                    icon={<ChevronLeft />}
                                    kind="secondary"
                                    size="compact"
                                    disabled={!entryLastMessageAt}
                                    loading={isPreviousTicketLoading}
                                    onClick={() => {
                                      this.onFetchPreviousTicket();
                                      trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
                                        moveTo: "previous",
                                        method: "arrow",
                                      });
                                    }}
                                  />
                                }
                                hideTooltip={shouldSeeNavigationInTicketTooltip}
                                title="Previous ticket"
                                firstKey={isMacUser ? "⌃" : "ctrl"}
                                secondKey={isMacUser ? "⌥" : "alt"}
                                thirdKey="J"
                                offsetX={-5}
                              />
                              <ShortcutTooltip
                                trigger={
                                  <Button
                                    icon={<ChevronRight />}
                                    kind="secondary"
                                    size="compact"
                                    disabled={!entryLastMessageAt}
                                    loading={isNextTicketLoading}
                                    onClick={() => {
                                      this.onFetchNextTicket();
                                      trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
                                        moveTo: "next",
                                        method: "arrow",
                                      });
                                    }}
                                  />
                                }
                                hideTooltip={shouldSeeNavigationInTicketTooltip}
                                title="Next ticket"
                                firstKey={isMacUser ? "⌃" : "ctrl"}
                                secondKey={isMacUser ? "⌥" : "alt"}
                                thirdKey="K"
                                offsetX={-5}
                              />
                            </Flex>
                          </Shortcuts>
                        )}
                      </Flex>
                      <OpenDetailsWrapper onClick={this.handleOpenDetails}>
                        <Icon source={Menu} />
                      </OpenDetailsWrapper>
                    </Flex>
                  </>
                }
              >
                {isFetching && (
                  <LoaderWrapper>
                    <Loader size="large" />
                  </LoaderWrapper>
                )}
                {isFetchingClosestTicket && (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    css={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: StackingContextLevel.ContentOverlay,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: withVar(DesignToken.SurfacePrimaryDefault),
                        opacity: 0.6,
                      },
                    }}
                  />
                )}
                {!isFetching && (
                  <TicketWrapper>
                    {shouldSeeTicketToolbar ? (
                      <TicketToolbar
                        ticket={ticket}
                        shortcutsConfig={this.shortcutsConfig}
                        shouldSeeNavigationInTicketTooltip={shouldSeeNavigationInTicketTooltip}
                        handleFetchNextTicket={this.onFetchNextTicket}
                        handleFetchPreviousTicket={this.onFetchPreviousTicket}
                        isMacUser={isMacUser}
                        isPreviousTicketLoading={isPreviousTicketLoading}
                        isNextTicketLoading={isNextTicketLoading}
                        entryLastMessageAt={entryLastMessageAt}
                        canManageTickets={canManageTickets}
                        isMemberOfTicketTeam={isMemberOfTicketTeam}
                        handleSiloChange={this.onSiloChange}
                        isMobile={isMobile}
                        onDeleteTicket={this.handleDeleteTicketModalOpen}
                      />
                    ) : (
                      <>
                        {!isRecentTicket && (
                          <TicketSiloLabel
                            ticket={ticket}
                            disabled={isChildTicket || !hasFullTicketAccess}
                            handleSiloChange={this.onSiloChange}
                          />
                        )}
                        {ticket.parentTicket && (
                          <StyledAccessWarning>
                            <StyledAccessWarningIcon name={IconsEnum.Info} width="15px" height="15px" inline />
                            This ticket has a browse-only option as it was merged with ticket&nbsp;
                            <StyledLink to={`/tickets/${ticket.parentTicket.ID}`} onClick={this.handleParentClick}>
                              {ticket.parentTicket.shortID}
                            </StyledLink>
                            .
                          </StyledAccessWarning>
                        )}
                        {canManageTickets && !isMemberOfTicketTeam && (
                          <StyledAccessWarning>
                            <StyledAccessWarningIcon name={IconsEnum.Info} width="15px" height="15px" inline />
                            You’re not a member of the team responsible for this ticket. You can only send private notes
                            to other agents.
                          </StyledAccessWarning>
                        )}
                      </>
                    )}
                    <TicketItemWrapper>
                      <TicketItem
                        currentAgent={currentAgent}
                        licenseSettings={licenseSettings}
                        ticket={ticket}
                        agents={agents}
                        teams={teams}
                        rules={rules}
                        tags={tags}
                        attachments={attachments}
                      />
                    </TicketItemWrapper>
                    {isRecentTicket && !isChildTicket && (
                      <TicketMessageBoxWrapper>
                        <MacrosPromoBanner />
                        <AttachmentProvider ticketID={ticket.id} onTransactionChange={this.handleTransactionID}>
                          <TicketMessageBox
                            key={this.getMessageBoxReactKey(hasFullTicketAccess)}
                            agentSignature={currentAgent.signature}
                            agentId={currentAgent.id}
                            cannedResponsesByShortcuts={cannedResponsesByShortcuts}
                            ticketId={ticket.id}
                            ticketStatus={ticket.status}
                            canSendPublicMessage={hasFullTicketAccess}
                            canSendPrivateMessage={canSendPrivateMessage}
                            notifyTicketTyping={notifyTicketTyping}
                            notifyStopTicketTyping={notifyStopTicketTyping}
                            isSendingMessage={sendingMessage}
                            onMessageSend={this.handleMessageSend}
                            onDraftSave={this.handleDraftSave}
                            onAttachmentsChange={this.handleAttachmentsChange}
                            // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
                            transactionID={this.state.transactionID}
                            blockEditForMerged={isChildTicket}
                          />
                        </AttachmentProvider>
                      </TicketMessageBoxWrapper>
                    )}
                    {!isRecentTicket && (
                      <TicketBottomSiloLabel
                        disabled={isChildTicket || !hasFullTicketAccess}
                        handleSiloChange={this.onSiloChange}
                        ticket={ticket}
                      />
                    )}
                  </TicketWrapper>
                )}
              </ViewContainer>
            </ThemeProvider>
          </ViewStack>
          <ViewStack>
            <ThemeProvider theme={legacyTheme}>
              <TicketDetails
                ticket={ticket}
                tags={tags}
                allTags={allTags}
                agents={agents}
                teams={teams}
                isFetching={isFetching && (!ticket || !loadingEvents)}
                canManageTicket={!isChildTicket && hasFullTicketAccess}
                canDeleteTicket={!isChildTicket && canDeleteTicket}
                onAssigneeChange={this.handleAssigneeChange}
                onAssignedTeamChange={this.handleAssignedTeamChange}
                onRequesterModalOpen={this.handleRequesterModalOpen}
                onMergingModalOpen={this.handleMergingModalOpen}
                onCcsModalOpen={this.handleCcsModalOpen}
                tagsUsageAllowed={!isChildTicket && tagsUsageAllowed}
                onTagAdd={this.handleTagAdd}
                onTagRemove={this.handleTagRemove}
                onStatusUpdate={this.handleStatusUpdate}
                onTicketUrlCopy={onTicketUrlCopy}
                onDeleteTicket={this.handleDeleteTicketModalOpen}
                isAgentInTicketTeam={isAgentInTicketTeam}
                isChildTicket={isChildTicket}
                isMobile={isMobile}
              />
            </ThemeProvider>
          </ViewStack>
        </Grid>
        {subjectModal && (
          <ChangeTicketSubjectModal
            subject={ticket.subject}
            onClose={this.handleSubjectModalClose}
            onSubmit={this.handleSubjectModalSubmit}
          />
        )}
        {requesterModal && (
          <ChangeTicketRequesterModal
            requesterName={ticket.requester.name}
            requesterEmail={ticket.requester.email}
            ccs={ticket.ccs}
            onClose={this.handleRequesterModalClose}
            onSubmit={this.handleRequesterModalSubmit}
          />
        )}
        {ccsModal && (
          <ThemeProvider theme={legacyTheme}>
            <ChangeTicketCcsModal
              emails={ccsEmails}
              requester={ticket.requester}
              agentsEmails={agents.map(agent => agent.email)}
              onClose={this.handleCcsModalClose}
              onSubmit={this.handleCcsModalSubmit}
            />
          </ThemeProvider>
        )}
        {deleteModal && (
          <DeleteTicketModal onClose={this.handleDeleteModalClose} onSubmit={this.handleDeleteModalSubmit} />
        )}
        {spamModal && (
          <ThemeProvider theme={legacyTheme}>
            <SpamListModal
              modalType={spamModal[0]}
              requesterEmails={spamModal[1]}
              onClose={this.handleSpamModalClose}
            />
          </ThemeProvider>
        )}
        {mergingModal && (
          <ThemeProvider theme={legacyTheme}>
            <MergeTicketModal
              ticket={ticket}
              ticketsToMerge={ticketsToMerge}
              onClose={this.handleMergingModalClose}
              mergeTickets={mergeTickets}
              fetchTicketsToMerge={fetchTicketsToMerge}
              tags={allTags}
              teams={teams}
              agents={agents}
            />
          </ThemeProvider>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  ticket: getTicket(state) as ITicket, // current is non-null in this route
  route: getRoute(state),
  agents: selectAgentsAlphabetically(state),
  cannedResponsesByShortcuts: selectAgentCannedResponsesByShortcuts(state),
  currentAgent: selectLoggedInAgent(state),
  focusedColumn: getFocusedColumn(state),
  teams: selectTeamsAlphabetically(state),
  tags: selectAgentTagsAlphabetically(state),
  allTags: selectTagsAlphabetically(state),
  rules: selectRules(state),
  //  @ts-expect-error TS(2554)
  tagsUsageAllowed: getTagsUsageAllowed(state),
  sendingMessage: isSendingMessage(state),
  unspamming: isUnspamming(state),
  canDeleteTickets: selectCanDeleteTickets(state),
  canManageTickets: selectCanManageTickets(state),
  viewingAgentIDs: selectTicketViewers(state, getTicket(state)?.id),
  recentTickets: selectRecentTickets(state),
  ticketsToMerge: selectTicketsToMerge(state),
  isFetchingClosestTicket: selectIsFetchingClosestTicket(state),
  hasSeenNavigationInTicketTooltip: selectHasSeenNavigationInTicketTooltip(state),
  shouldSeeTicketToolbar: selectTicketFeedToolbar(state),
  licenseSettings: selectLicenseSettings(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onTicketOpen: (id: string) => {
    //  @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.fetchSingle(id, true));
  },
  // @ts-expect-error TS(2345)
  onDelete: (id: string) => dispatch(TicketsViewActions.deleteTicket(id, true)),
  onAssigneeChange: (ticketId: string, assigneeId: string) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.updateAssignee({ ticketId, assigneeId })),
  onAssignedTeamChange: (ticketId: string, teamId: string) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.updateAssignedTeam({ ticketId, teamId })),
  onTagsAdd: (ticketId: string, tagIds: string[], suggestedTagIds?: string[]) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketActions.addTags({ ticketId, tagIds, suggestedTagIds })),
  onTagRemove: (ticketId: string, tagId: string) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketActions.removeTag({ ticketId, tagId })),
  // @ts-expect-error TS(2345)
  onSubjectChange: (ticketId: string, subject: string) => dispatch(TicketsViewActions.updateSubject(ticketId, subject)),
  onRequesterChange: (ticketId: string, requesterName: string, requesterEmail: string) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.updateRequester(ticketId, requesterName, requesterEmail)),
  onStatusUpdate: (ticketId: string, status: TicketStatus, usingShortcut?: boolean) =>
    // @ts-expect-error TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
    dispatch(TicketsViewActions.updateStatus({ ticketId, status, interaction: "ticketDetails", usingShortcut })),
  // @ts-expect-error TS(2345)
  onCcsChange: (ticketId: string, emails: string[]) => dispatch(TicketsViewActions.updateCCs(ticketId, emails)),
  // @ts-expect-error TS(2345)
  onMessageSend: (payload: ITicketsViewSendMessagePayload) => dispatch(TicketsViewActions.sendMessage(payload)),
  onDraftSave: (
    ticketId: string,
    message: string | Descendant[],
    isPrivate: boolean,
    status: TicketStatus,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ) =>
    dispatch(
      // @ts-expect-error TS(2345)
      TicketsViewActions.saveDraft({
        ticketId,
        message,
        isPrivate,
        status,
        cannedResponseIds,
        replySuggestionIds,
      })
    ),
  // @ts-expect-error TS(2345)
  onDraftClear: (ticketId: string) => dispatch(TicketsViewActions.clearDraft({ ticketId })),
  // @ts-expect-error TS(2345)
  onUnspam: (ticketId: string) => dispatch(TicketsViewActions.unspam({ ticketId })),
  // @ts-expect-error TS(2345)
  onTicketUrlCopy: () => dispatch(TicketsViewActions.copyTicketUrl()),
  // @ts-expect-error TS(2345)
  onSetFocusedColumn: (focusedColumn: number) => dispatch(TicketsViewActions.setFocusedColumn(focusedColumn)),
  // @ts-expect-error TS(2345)
  onSiloUpdate: (ticketId: string, silo: TicketSilo) => dispatch(TicketActions.updateSilo({ silo, ticketId })),
  // @ts-expect-error TS(2345)
  notifyTicketViewing: (id: string) => dispatch(TicketsViewActions.notifyViewing(id)),
  // @ts-expect-error TS(2345)
  notifyStopTicketViewing: (id: string) => dispatch(TicketsViewActions.notifyViewingStopped(id)),
  // @ts-expect-error TS(2345)
  notifyTicketTyping: (id: string) => dispatch(TicketsViewActions.notifyTyping(id)),
  // @ts-expect-error TS(2345)
  notifyStopTicketTyping: (id: string) => dispatch(TicketsViewActions.notifyTypingStop(id)),
  // @ts-expect-error TS(2345)
  mergeTickets: (payload: IMergeTicketsPayload) => dispatch(TicketsViewActions.mergeTickets(payload)),
  // @ts-expect-error TS(2345)
  fetchTicketsToMerge: (payload: IFetchTicketsToMergePayload) => dispatch(TicketActions.fetchtTicketsToMerge(payload)),
  fetchNextTicket: (payload: IFetchClosestTicketInfoPayload) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.fetchClosestTicket(payload)),
  fetchPreviousTicket: (payload: IFetchClosestTicketInfoPayload) =>
    // @ts-expect-error TS(2345)
    dispatch(TicketsViewActions.fetchClosestTicket(payload)),
  setAgentFlag: (name: string, value: boolean) =>
    dispatch(
      // @ts-expect-error TS(2345)
      AgentActions.setFlag({
        name,
        value,
      })
    ),
});

export const Ticket = withRouter(connect(mapStateToProps, mapDispatchToProps)(withBreakpoints(UnconnectedTicket)));
